import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { ACTIVE, SIGNED_IN, SIGNOUT_REASON, auth, currentAal, db, getSession, mfa, onSession, type Factor, type Session } from "./api";

export type Access = "none" | "view" | "edit";
export type Scope = "own" | "downline" | "all";
export type Perms = Record<string, { access: Access; scope: Scope }>;
export type Profile = { user_id: string; display_name: string; email: string; role_key: string; team_member_id: string | null };

type Ctx = {
  session: Session | null;
  profile: Profile | null;
  perms: Perms;
  ready: boolean;
  can: (module: string, level?: "view" | "edit") => boolean;
  refresh: () => Promise<void>;
  /** needs = has 2FA on but this session hasn't passed it; mustEnroll = 2FA required for everyone but not set up yet */
  mfaState: { needs: boolean; mustEnroll: boolean };
};

const SessionCtx = createContext<Ctx>(null as unknown as Ctx);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(getSession());
  const [profile, setProfile] = useState<Profile | null>(null);
  const [perms, setPerms] = useState<Perms>({});
  const [ready, setReady] = useState(false);
  const [mfaState, setMfaState] = useState({ needs: false, mustEnroll: false });
  const [limits, setLimits] = useState({ idle: 30, days: 7 });

  async function loadProfile(s: Session | null) {
    if (!s) {
      setProfile(null);
      setPerms({});
      setReady(true);
      return;
    }
    try {
      const [factors, sec] = await Promise.all([
        mfa.factors().catch(() => [] as Factor[]),
        db.one<{ value: any }>("settings", { key: "eq.security", select: "value" }).catch(() => null),
      ]);
      const hasVerified = factors.some((f) => f.status === "verified");
      setLimits({ idle: Number(sec?.value?.idle_minutes ?? 30), days: Number(sec?.value?.max_session_days ?? 7) });
      setMfaState({ needs: hasVerified && currentAal() !== "aal2", mustEnroll: !hasVerified && !!sec?.value?.require_mfa });
      const [p, rows] = await Promise.all([
        db.one<Profile>("profiles", { user_id: `eq.${s.user.id}`, select: "user_id,display_name,email,role_key,team_member_id" }),
        db.rpc<{ module: string; access: Access; scope: Scope }[]>("my_permissions"),
      ]);
      setProfile(p);
      setPerms(Object.fromEntries(rows.map((r) => [r.module, { access: r.access, scope: r.scope }])));
    } catch {
      setProfile(null);
      setPerms({});
    } finally {
      setReady(true);
    }
  }

  useEffect(() => {
    let alive = true;
    (async () => {
      await auth.consumeRedirect().catch(() => false);
      if (alive) await loadProfile(getSession());
    })();
    const off = onSession((s) => {
      setSession(s);
      loadProfile(s);
    });
    return () => {
      alive = false;
      off();
    };
  }, []);

  // Sign out after a stretch without activity, and ask to sign in again every few days
  useEffect(() => {
    if (!session) return;
    const get = (k: string) => { try { return Number(localStorage.getItem(k)) || 0; } catch { return 0; } };
    const put = (k: string, v: number) => { try { localStorage.setItem(k, String(v)); } catch { /* private mode */ } };
    if (!get(SIGNED_IN)) put(SIGNED_IN, Date.now());
    if (!get(ACTIVE)) put(ACTIVE, Date.now());
    let last = 0;
    const touch = () => { const n = Date.now(); if (n - last > 15_000) { last = n; put(ACTIVE, n); } };
    const ev = ["pointerdown", "keydown", "scroll", "touchstart", "mousemove"];
    ev.forEach((e) => window.addEventListener(e, touch, { passive: true }));
    const out = (why: string) => { try { localStorage.setItem(SIGNOUT_REASON, why); } catch { /* fine */ } auth.signOut(); };
    const check = () => {
      const n = Date.now();
      if (limits.idle > 0 && n - get(ACTIVE) > limits.idle * 60_000) out(`You were signed out after ${limits.idle} minutes without activity, to keep client information safe.`);
      else if (limits.days > 0 && n - get(SIGNED_IN) > limits.days * 86_400_000) out(`For security, please sign in again (every ${limits.days} days).`);
    };
    check();
    const t = window.setInterval(check, 30_000);
    const vis = () => document.visibilityState === "visible" && check();
    document.addEventListener("visibilitychange", vis);
    return () => { ev.forEach((e) => window.removeEventListener(e, touch)); window.clearInterval(t); document.removeEventListener("visibilitychange", vis); };
  }, [session, limits.idle, limits.days]);

  const can = (module: string, level: "view" | "edit" = "view") => {
    const a = perms[module]?.access ?? "none";
    return level === "view" ? a !== "none" : a === "edit";
  };

  return (
    <SessionCtx.Provider value={{ session, profile, perms, ready, can, mfaState, refresh: () => loadProfile(getSession()) }}>
      {children}
    </SessionCtx.Provider>
  );
}

export const useSession = () => useContext(SessionCtx);
